// src/api/sales.js
import { api } from "./client";

/**
 * @typedef {Object} SalesMeta
 * @property {number} current_page
 * @property {number} per_page
 * @property {number} last_page
 * @property {number} total
 *
 * @typedef {Object} SalesResponse
 * @property {Array<any>} items
 * @property {SalesMeta} meta
 * @property {{ next: string|null, prev: string|null }} links
 */

/** Helper: pastikan angka */
const N = (v, fallback = 0) => (v == null ? fallback : Number(v));

/**
 * Normalisasi berbagai bentuk respons backend menjadi { items, meta, links }.
 * Mendukung:
 *  - Array murni
 *  - Laravel paginator default (root fields)
 *  - Payload sudah dinormalisasi { items, meta, links }
 *  - Fallback aman
 * @param {any} d
 * @param {{ per_page?: number }} params
 * @returns {SalesResponse}
 */
function normalizeSalesResponse(d, params = {}) {
  // 1) Array murni
  if (Array.isArray(d)) {
    const total = d.length;
    return {
      items: d,
      meta: { current_page: 1, per_page: total, last_page: 1, total },
      links: { next: null, prev: null },
    };
  }

  // 2) Laravel paginator default: { current_page, data, per_page, last_page, total, ... }
  if (d && typeof d === "object" && Array.isArray(d.data)) {
    const per = N(d.per_page, d.data.length || N(params.per_page, 10));
    const total = N(d.total, d.data.length);
    const last = d.last_page != null ? N(d.last_page, 1) : Math.max(1, Math.ceil(total / Math.max(1, per)));
    return {
      items: d.data,
      meta: {
        current_page: N(d.current_page, 1),
        per_page: per,
        last_page: last,
        total,
      },
      links: {
        next: d.next_page_url ?? null,
        prev: d.prev_page_url ?? null,
      },
    };
  }

  // 3) Sudah dinormalisasi: { items, meta, links }
  if (d && Array.isArray(d.items) && d.meta) {
    const per = N(d.meta.per_page, d.items.length || N(params.per_page, 10));
    const total = N(d.meta.total, d.items.length);
    const last =
      d.meta.last_page != null ? N(d.meta.last_page, 1) : Math.max(1, Math.ceil(total / Math.max(1, per)));
    return {
      items: d.items,
      meta: {
        current_page: N(d.meta.current_page, 1),
        per_page: per,
        last_page: last,
        total,
      },
      links: d.links ?? { next: null, prev: null },
    };
  }

  // 4) Fallback aman
  const arr = Array.isArray(d?.data) ? d.data : [];
  const per = N(d?.per_page, N(params.per_page, 10));
  const total = N(d?.total, arr.length);
  const last = d?.last_page != null ? N(d.last_page, 1) : Math.max(1, Math.ceil(total / Math.max(1, per)));
  return {
    items: arr,
    meta: {
      current_page: N(d?.current_page, 1),
      per_page: per,
      last_page: last,
      total,
    },
    links: {
      next: d?.next_page_url ?? null,
      prev: d?.prev_page_url ?? null,
    },
  };
}

/**
 * BUAT TRANSAKSI BARU
 * @param {Object} payload
 * @returns {Promise<any>}
 */
export async function createSale(payload) {
  const { data } = await api.post("/api/sales", payload);
  return data;
}

/**
 * DETAIL 1 TRANSAKSI
 * @param {string|number} id
 * @returns {Promise<any>}
 */
export async function getSale(id) {
  const { data } = await api.get(`/api/sales/${id}`);
  return data?.data || data;
}

/**
 * LIST TRANSAKSI (server-side)
 * GET /api/sales?search=&payment_method=&date_from=&date_to=&page=&per_page=&sort=&dir=
 * @param {Object} params
 * @param {AbortSignal} [signal]
 * @returns {Promise<SalesResponse>}
 */
export async function getSales(params = {}, signal) {
  const res = await api.get("/api/sales", { params, signal });
  return normalizeSalesResponse(res.data, params);
}

/**
 * VOID / REJECT TRANSAKSI
 * @param {string|number} saleId
 * @param {Object} payload
 * @param {AbortSignal} [signal]
 * @returns {Promise<any>}
 */
export async function voidSale(saleId, payload = {}, signal) {
  const { data } = await api.post(`/api/sales/${saleId}/void`, payload, { signal });
  return data;
}

export async function acknowledgeSaleReview(saleId, signal) {
  const { data } = await api.post(`/api/sales/${saleId}/acknowledge-review`, {}, { signal });
  return data;
}

/** Backend caps per_page (200 without items, 50 with). Walk pages for a full range. */
const SALES_PAGE_SIZE = 200;
const SALES_PAGE_SIZE_WITH_ITEMS = 50;
const SALES_MIN_PAGE_SIZE = 5;
const SALES_MAX_PAGES = 500;

function isTruncatedSalesError(err) {
  const msg = String(err?.message || err || "");
  return (
    err?.name === "SyntaxError" ||
    /Unexpected end of (JSON )?input/i.test(msg) ||
    /Unexpected token/i.test(msg) ||
    /terpotong/i.test(msg)
  );
}

/** Fetch every page of /api/sales for the given filters (from, to, status, code, …). */
export async function listAllSales(params = {}, signal, options = {}) {
  const { includeItems = false } = options;
  const all = [];
  let page = 1;
  let lastPage = 1;
  let pageSize = includeItems ? SALES_PAGE_SIZE_WITH_ITEMS : SALES_PAGE_SIZE;

  do {
    const pageParams = {
      ...params,
      page,
      per_page: pageSize,
    };
    if (!includeItems) {
      pageParams.without_items = 1;
    }

    try {
      const { items, meta } = await getSales(pageParams, signal);
      all.push(...items);
      lastPage = meta?.last_page ?? 1;
      page += 1;
      if (!items?.length) break;
    } catch (err) {
      if (signal?.aborted) throw err;
      if (isTruncatedSalesError(err) && pageSize > SALES_MIN_PAGE_SIZE) {
        // Page index depends on per_page — restart with a smaller chunk.
        pageSize = Math.max(
          SALES_MIN_PAGE_SIZE,
          Math.floor(pageSize / 2)
        );
        all.length = 0;
        page = 1;
        lastPage = 1;
        continue;
      }
      throw err;
    }
  } while (page <= lastPage && page <= SALES_MAX_PAGES);

  return all;
}

export async function listSalesForDashboard(params = {}, signal) {
  return listAllSales(params, signal, { includeItems: true });
}